<template>
    <div
        class="container mx-auto hidden h-full flex-row items-center justify-between gap-x-12 xl:flex xl:gap-x-6 2xl:gap-x-12"
    >
        <LogoLink />
        <nav class="flex flex-1 items-center">
            <NavigationMenuRoot
                id="nav-menu-root"
                :model-value="openMenuValue"
                :disable-click-trigger="true"
                :disable-hover-trigger="true"
                :disable-pointer-leave-close="true"
                @update:model-value="handleRekaUIChange"
            >
                <NavigationMenuList class="flex items-center">
                    <NavigationMenuItem
                        v-for="(menuDropdown, index) in data?.menu_dropdowns"
                        :id="`menu-${index}`"
                        :key="index"
                        :value="String(index)"
                    >
                        <NavigationMenuTrigger
                            class="group flex h-[80px] w-full items-center justify-between border-b-[3px] border-white px-2 font-semibold text-grind transition-all hover:text-brew data-[state=open]:border-brew 2xl:px-3.5"
                            @pointerenter="
                                handleTriggerHover($event, String(index))
                            "
                            @click="handleTriggerClick(String(index))"
                        >
                            <span class="mr-1.5 font-semibold text-inherit">
                                {{ menuDropdown.title }}
                            </span>
                            <span
                                class="-rotate-90 transition-transform lg:rotate-0 group-data-[state=open]:lg:rotate-180"
                            >
                                <Icon
                                    name="chevron-down"
                                    class="text-xs text-inherit"
                                />
                            </span>
                        </NavigationMenuTrigger>

                        <NavigationMenuContent
                            force-mount
                            data-navigation-menu-content
                            class="absolute left-1/2 top-full z-10 w-screen -translate-x-1/2 border-t border-grind-200 bg-white focus:outline-none"
                            @pointerenter="
                                handleTriggerHover($event, String(index))
                            "
                            @pointerleave="handleContentLeave($event)"
                            @pointer-down-outside="
                                suppressDismissForInactivePanel(
                                    $event,
                                    String(index),
                                )
                            "
                        >
                            <div
                                class="container my-4 px-8"
                                :class="[
                                    menuDropdown.columns.length === 2 &&
                                        'max-w-4xl',
                                ]"
                            >
                                <div
                                    class="flex justify-center"
                                    :class="[
                                        menuDropdown.columns_separator &&
                                            'divide-x divide-grind-200',
                                    ]"
                                >
                                    <div
                                        v-for="(
                                            column, columnIndex
                                        ) in menuDropdown.columns"
                                        :key="columnIndex"
                                        class=""
                                        :class="[
                                            'first:pl-0 last:pr-0',
                                            'thumbnails' in column
                                                ? 'px-5 2xl:px-9'
                                                : 'px-6',
                                            'thumbnails' in column &&
                                            menuDropdown.columns.length === 1
                                                ? 'w-1/3'
                                                : '',
                                            'thumbnails' in column &&
                                            menuDropdown.columns.length > 1
                                                ? 'flex-1'
                                                : '',
                                        ]"
                                    >
                                        <!-- Menu Group Thumbnails -->
                                        <div v-if="'thumbnails' in column">
                                            <div
                                                v-if="column.title"
                                                class="mb-1 text-sm font-bold uppercase tracking-wide"
                                            >
                                                {{ column.title }}
                                            </div>
                                            <p
                                                v-if="column.description"
                                                class="mb-4 text-sm text-grind-800"
                                            >
                                                {{ column.description }}
                                            </p>
                                            <div
                                                class="grid grid-flow-col grid-cols-2 gap-3"
                                                :style="{
                                                    gridTemplateRows: `repeat(${Math.ceil(column.thumbnails.length / 2)}, minmax(0, 1fr))`,
                                                }"
                                            >
                                                <NuxtLink
                                                    v-for="(
                                                        thumb, thumbIndex
                                                    ) in column.thumbnails"
                                                    :key="thumbIndex"
                                                    :to="
                                                        isActive
                                                            ? urlFromStoryblokLinkField(
                                                                  thumb.link,
                                                              )
                                                            : undefined
                                                    "
                                                    class="group relative flex items-center gap-2 rounded-2xl bg-gray-100 py-1 pl-1 pr-2 transition-all hover:bg-gray-300"
                                                    @click="
                                                        handleRekaUIChange(
                                                            undefined,
                                                        )
                                                    "
                                                >
                                                    <StoryblokImage
                                                        :src="thumb.image.src"
                                                        :alt="
                                                            thumb.image.alt ||
                                                            ''
                                                        "
                                                        class="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-xl object-contain"
                                                        sizes="80px"
                                                        :image-width="80"
                                                        :image-height="80"
                                                    />
                                                    <div
                                                        class="menu-thumbnail-title text-sm leading-tight transition-colors group-hover:underline"
                                                        :data-tag="
                                                            thumb.display_tag &&
                                                            thumb.tag_text
                                                                ? thumb.tag_text
                                                                : null
                                                        "
                                                        :data-tag-color="
                                                            thumb.display_tag &&
                                                            thumb.tag_color
                                                                ? thumb.tag_color
                                                                : null
                                                        "
                                                    >
                                                        {{ thumb.title }}
                                                    </div>
                                                </NuxtLink>
                                            </div>
                                        </div>

                                        <!-- Menu Group Links -->
                                        <ul
                                            v-else-if="'links' in column"
                                            class="flex flex-col gap-y-2"
                                        >
                                            <li v-if="column.title">
                                                <div
                                                    class="mb-1 text-sm font-bold uppercase tracking-wide"
                                                >
                                                    {{ column.title }}
                                                </div>
                                            </li>
                                            <li
                                                v-for="(
                                                    link, linkIndex
                                                ) in column.links"
                                                :key="linkIndex"
                                            >
                                                <NuxtLink
                                                    :to="
                                                        isActive
                                                            ? urlFromStoryblokLinkField(
                                                                  link,
                                                              ) +
                                                              (link.anchor
                                                                  ? '#' +
                                                                    link.anchor
                                                                  : '')
                                                            : undefined
                                                    "
                                                    class="text-grind transition-colors hover:text-brew hover:underline"
                                                    @click="
                                                        handleRekaUIChange(
                                                            undefined,
                                                        )
                                                    "
                                                >
                                                    {{ link.label }}
                                                </NuxtLink>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                </NavigationMenuList>
            </NavigationMenuRoot>

            <!-- Dark overlay when menu is open -->
            <div
                v-show="openMenuValue"
                class="fixed inset-0 left-0 right-0 top-48 z-0 bg-black/25"
            />

            <NuxtLink
                v-if="data?.featured_link"
                :to="isActive ? data.featured_link.url : undefined"
                class="group ml-3.5 flex items-center whitespace-nowrap py-3 text-vibe transition-all hover:text-brew"
            >
                <Icon name="left-accent-icon" class="text-xl" />
                <span class="font-accent text-xl">{{
                    data.featured_link_text || data.featured_link.label
                }}</span>
                <Icon name="right-accent-icon" class="text-xl" />
            </NuxtLink>

            <div
                v-if="data?.action_buttons && data.action_buttons.length > 0"
                class="px-4 lg:ml-auto lg:flex lg:flex-none lg:justify-end lg:px-0"
            >
                <ButtonContainer>
                    <div
                        v-for="(
                            actionButton, actionIndex
                        ) in data.action_buttons"
                        :key="actionIndex"
                        class="flex gap-2"
                    >
                        <NuxtLink
                            v-for="(btn, btnIndex) in actionButton.button"
                            :key="btnIndex"
                            :to="
                                isActive
                                    ? urlFromStoryblokLinkField(btn.link)
                                    : undefined
                            "
                            :target="btn.link?.target"
                            :class="'btn-' + btn.style"
                            class="!inline-flex items-center gap-2.5 px-5 py-3.5"
                        >
                            <Icon
                                v-if="actionButton.icon"
                                :name="actionButton.icon"
                                class="text-[20px]"
                            />
                            {{ btn.label }}
                        </NuxtLink>
                    </div>
                </ButtonContainer>
            </div>
        </nav>
    </div>
</template>

<script setup lang="ts">
import {
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuRoot,
    NavigationMenuTrigger,
} from 'reka-ui';
import type { SiteNavType } from '#shared/types/SiteNavType';
import LogoLink from '~/layouts/includes/menu/LogoLink.vue';
import { urlFromStoryblokLinkField } from '~/utils/helpers';

withDefaults(
    defineProps<{
        data: SiteNavType | null;
        isActive?: boolean;
    }>(),
    {
        isActive: true,
    },
);

const openMenuValue = ref<string | undefined>(undefined);
const lockedMenuValue = ref<string | undefined>(undefined);

const handleRekaUIChange = (value: string | undefined) => {
    openMenuValue.value = value;

    // Unlock the menu when it's closed
    if (!value) {
        lockedMenuValue.value = undefined;
    }
};

const handleTriggerClick = (index: string) => {
    if (lockedMenuValue.value === index) {
        // Unlock and close if clicking the same trigger
        handleRekaUIChange(undefined);
    } else {
        // Lock this menu open
        lockedMenuValue.value = index;
        handleRekaUIChange(index);
    }
};

const handleTriggerHover = (event: PointerEvent, index: string) => {
    // Only respond to mouse input, not touch or pen
    if (event.pointerType !== 'mouse') {
        return;
    }

    // Only respond to hover if there's no locked menu
    if (!lockedMenuValue.value) {
        handleRekaUIChange(index);
    }
};

const suppressDismissForInactivePanel = (event: Event, panelIndex: string) => {
    if (openMenuValue.value !== panelIndex) {
        event.preventDefault();
    }
};

const handleContentLeave = (event: PointerEvent) => {
    // Only respond to mouse input, not touch or pen
    if (event.pointerType !== 'mouse') {
        return;
    }

    // Only close on leave if there's no locked menu
    if (!lockedMenuValue.value) {
        handleRekaUIChange(undefined);
    }
};
</script>
<style scoped>
nav#nav-menu-root > div:first-child {
    position: static !important;
}

:deep([data-navigation-menu-content][data-state='closed']) {
    display: none;
}

.menu-thumbnail-title[data-tag]::before {
    content: attr(data-tag);
    position: absolute;
    bottom: 0.25rem;
    display: inline-block;
    border-radius: 0.25rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 700;
    color: white;
}
.menu-thumbnail-title[data-tag-color='brew']::before {
    background-color: theme('colors.brew');
}
.menu-thumbnail-title[data-tag-color='spice']::before {
    background-color: theme('colors.spice');
}
.menu-thumbnail-title[data-tag-color='vibe']::before {
    background-color: theme('colors.vibe.DEFAULT');
}
.menu-thumbnail-title[data-tag-color='drip']::before {
    background-color: theme('colors.drip');
}
.menu-thumbnail-title[data-tag-color='grind']::before {
    background-color: theme('colors.grind.DEFAULT');
}
</style>
